import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Label, PageHeader, Textarea } from '@/components/ui'
import { cn } from '@/lib/cn'
import {
  ArrowRight,
  BarChart3,
  BellRing,
  CalendarClock,
  CheckCircle2,
  Mail,
  Megaphone,
  MessageSquare,
  MousePointerClick,
  Play,
  Plus,
  Repeat2,
  Send,
  Sparkles,
  Target,
  Users,
  WandSparkles,
} from 'lucide-react'

type Channel = 'email' | 'whatsapp' | 'sms'
type CampaignStatus = 'draft' | 'scheduled' | 'sent'
type TemplateKind = 'promo' | 'launch' | 'abandoned_cart' | 'winback'
type AutomationType = 'welcome' | 'abandoned_cart' | 'post_purchase' | 'reengagement'

interface TemplateRecord {
  id: string
  kind: TemplateKind
  name: string
  subject: string
  preview: string
  channel: Channel
}

interface SegmentRecord {
  id: string
  name: string
  audience: string
  size: number
  channel: Channel
}

interface CampaignRecord {
  id: string
  name: string
  channel: Channel
  status: CampaignStatus
  segment: string
  sendAt: string
  opens: string
  clicks: string
}

interface AutomationRecord {
  id: string
  type: AutomationType
  name: string
  trigger: string
  channel: Channel
  active: boolean
}

const TEMPLATES: TemplateRecord[] = [
  { id: 'tpl-1', kind: 'promo', name: 'Flash Sale 24h', subject: 'Solo hoy: 20% OFF', preview: 'Oferta urgente con CTA directo y contador.', channel: 'email' },
  { id: 'tpl-2', kind: 'launch', name: 'Lanzamiento de colección', subject: 'Nueva colección disponible', preview: 'Mensaje premium para lanzamiento de productos.', channel: 'whatsapp' },
  { id: 'tpl-3', kind: 'abandoned_cart', name: 'Carrito abandonado', subject: 'Tu carrito sigue guardado', preview: 'Recordatorio con beneficio y prueba social.', channel: 'email' },
  { id: 'tpl-4', kind: 'winback', name: 'Reactivación', subject: 'Te extrañamos en Centrio', preview: 'Campaña para volver a activar clientes dormidos.', channel: 'sms' },
]

const SEGMENTS: SegmentRecord[] = [
  { id: 'seg-1', name: 'Clientes VIP', audience: 'Últimos 90 días, ticket alto', size: 1840, channel: 'email' },
  { id: 'seg-2', name: 'Carritos abandonados', audience: 'Interacción reciente sin compra', size: 620, channel: 'whatsapp' },
  { id: 'seg-3', name: 'Prospectos nuevos', audience: 'Se registraron pero no compraron', size: 2910, channel: 'sms' },
  { id: 'seg-4', name: 'Clientes inactivos', audience: 'Sin compras en 120 días', size: 1120, channel: 'email' },
]

const CAMPAIGNS: CampaignRecord[] = [
  { id: 'cmp-1', name: 'Promo Fin de Semana', channel: 'email', status: 'scheduled', segment: 'Clientes VIP', sendAt: 'Hoy 6:00 p.m.', opens: '41%', clicks: '9.2%' },
  { id: 'cmp-2', name: 'Nuevo drop deportivo', channel: 'whatsapp', status: 'sent', segment: 'Prospectos nuevos', sendAt: 'Ayer 11:00 a.m.', opens: '78%', clicks: '21%' },
  { id: 'cmp-3', name: 'Recuperación Q2', channel: 'sms', status: 'draft', segment: 'Clientes inactivos', sendAt: 'Sin programar', opens: '—', clicks: '—' },
]

const AUTOMATIONS: AutomationRecord[] = [
  { id: 'auto-1', type: 'welcome', name: 'Bienvenida', trigger: 'Nuevo registro', channel: 'email', active: true },
  { id: 'auto-2', type: 'abandoned_cart', name: 'Carrito abandonado', trigger: '30 min sin compra', channel: 'whatsapp', active: true },
  { id: 'auto-3', type: 'post_purchase', name: 'Post-compra', trigger: 'Pedido entregado', channel: 'email', active: false },
  { id: 'auto-4', type: 'reengagement', name: 'Reactivación', trigger: '120 días sin actividad', channel: 'sms', active: false },
]

export const Route = createFileRoute('/store/$id/marketing')({
  component: MarketingPage,
})

function MarketingPage() {
  const [channel, setChannel] = useState<Channel>('email')
  const [templateKind, setTemplateKind] = useState<TemplateKind>('promo')
  const [subject, setSubject] = useState('Oferta especial para tu comunidad')
  const [message, setMessage] = useState('Hola {{nombre}}, tenemos una promo pensada para tu audiencia. CTA y beneficio principal aquí.')
  const [segment, setSegment] = useState(SEGMENTS[0]?.name ?? '')
  const [campaignName, setCampaignName] = useState('Campaña de marzo')

  const filteredTemplates = useMemo(() => TEMPLATES.filter((template) => template.channel === channel), [channel])
  const selectedTemplate = filteredTemplates.find((template) => template.kind === templateKind) ?? filteredTemplates[0] ?? TEMPLATES[0]!
  const selectedSegment = SEGMENTS.find((item) => item.name === segment) ?? SEGMENTS[0]!

  return (
    <>
      <PageHeader title="Mensajes masivos" description="Mockup UI para campañas, plantillas y automatizaciones de marketing." />
      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Campañas" value={CAMPAIGNS.length} icon={Megaphone} color="text-cyan-700 dark:text-cyan-400" />
          <StatCard label="Plantillas" value={TEMPLATES.length} icon={Mail} color="text-emerald-700 dark:text-emerald-400" />
          <StatCard label="Segmentos" value={SEGMENTS.length} icon={Target} color="text-[hsl(var(--primary))]" />
          <StatCard label="Alcance" value={selectedSegment.size} icon={Users} color="text-violet-700 dark:text-violet-400" suffix=" contactos" />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1.15fr_0.85fr] gap-6">
          <Card className="border-cyan-200 dark:border-cyan-900/30 bg-background/70">
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <Megaphone className="size-5 text-cyan-700 dark:text-cyan-400" /> Constructor de campaña
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">Define mensaje, audiencia, plantilla y canal de envío.</p>
                </div>
                <div className="flex rounded-lg border border-border bg-[hsl(var(--surface))] p-1">
                  {(['email', 'whatsapp', 'sms'] as const).map((item) => (
                    <button
                      key={item}
                      onClick={() => setChannel(item)}
                      className={cn(
                        'px-3 py-1.5 text-xs rounded-md transition inline-flex items-center gap-2',
                        channel === item ? 'bg-cyan-500 text-black font-medium' : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {item === 'email' && <Mail className="size-3.5" />}
                      {item === 'whatsapp' && <MessageSquare className="size-3.5" />}
                      {item === 'sms' && <Send className="size-3.5" />}
                      {item.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="campaign-name">Nombre de campaña</Label>
                  <Input id="campaign-name" value={campaignName} onChange={(e) => setCampaignName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="segment">Segmento</Label>
                  <select
                    id="segment"
                    value={segment}
                    onChange={(e) => setSegment(e.target.value)}
                    className="h-9 w-full rounded-md border border-border bg-[hsl(var(--surface))] px-3 text-sm text-foreground"
                  >
                    {SEGMENTS.filter((item) => item.channel === channel).map((item) => (
                      <option key={item.id} value={item.name}>{item.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject">Asunto / encabezado</Label>
                  <Input id="subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="template-kind">Plantilla</Label>
                  <select
                    id="template-kind"
                    value={templateKind}
                    onChange={(e) => setTemplateKind(e.target.value as TemplateKind)}
                    className="h-9 w-full rounded-md border border-border bg-[hsl(var(--surface))] px-3 text-sm text-foreground"
                  >
                    {filteredTemplates.map((template) => (
                      <option key={template.id} value={template.kind}>{template.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Mensaje</Label>
                <Textarea id="message" value={message} onChange={(e) => setMessage(e.target.value)} className="min-h-[180px]" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <MetricChip label="Personalización" value="Variables dinámicas listas" />
                <MetricChip label="Estado" value="Borrador" />
                <MetricChip label="Programación" value="Inmediata o futura" />
              </div>

              <div className="flex flex-wrap gap-3">
                <Button>
                  <Send className="size-4 mr-2" /> Enviar campaña
                </Button>
                <Button variant="outline">
                  <CalendarClock className="size-4 mr-2" /> Programar envío
                </Button>
                <Button variant="ghost">
                  <Sparkles className="size-4 mr-2" /> Generar versión corta
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-cyan-200 dark:border-cyan-900/30 bg-gradient-to-br from-cyan-100 dark:from-cyan-950/30 to-background">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle2 className="size-4 text-cyan-700 dark:text-cyan-400" /> Vista previa
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-2xl border border-cyan-200 dark:border-cyan-800/50 bg-background/60 p-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <Badge variant="success">{channel.toUpperCase()}</Badge>
                  <span className="text-xs text-muted-foreground">{selectedTemplate.name}</span>
                </div>
                <p className="text-lg font-semibold">{campaignName}</p>
                <p className="text-sm text-muted-foreground">{subject}</p>
                <div className="rounded-xl border border-border bg-[hsl(var(--surface))]/70 p-4 text-sm text-foreground leading-relaxed">
                  {message}
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Segmento: {selectedSegment.name}</span>
                  <span>{selectedSegment.size.toLocaleString('es-CO')} contactos</span>
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-[hsl(var(--surface))] p-4 text-sm space-y-2">
                <p className="text-foreground font-medium">Checklist visual</p>
                <p className="text-muted-foreground flex items-center gap-2"><MousePointerClick className="size-4" /> CTA visible y medible</p>
                <p className="text-muted-foreground flex items-center gap-2"><WandSparkles className="size-4" /> Variables dinámicas listas</p>
                <p className="text-muted-foreground flex items-center gap-2"><Target className="size-4" /> Segmentación por audiencia</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Plantillas por canal</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  className={cn(
                    'rounded-xl border p-4 transition',
                    template.kind === selectedTemplate.kind ? 'border-cyan-200 dark:border-cyan-700 bg-cyan-50 dark:bg-cyan-950/20' : 'border-border bg-[hsl(var(--surface))]',
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium">{template.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{template.subject}</p>
                      <p className="text-xs text-muted-foreground mt-2">{template.preview}</p>
                    </div>
                    <Badge variant={template.kind === templateKind ? 'success' : 'muted'}>{template.channel.toUpperCase()}</Badge>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Campañas recientes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {CAMPAIGNS.map((campaign) => (
                <div key={campaign.id} className="rounded-xl border border-border bg-[hsl(var(--surface))] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium">{campaign.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{campaign.segment} • {campaign.sendAt}</p>
                    </div>
                    <Badge variant={campaign.status === 'sent' ? 'success' : campaign.status === 'scheduled' ? 'warning' : 'muted'}>{campaign.status}</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-4 text-xs text-muted-foreground">
                    <div>
                      <p>Canal</p>
                      <p className="text-foreground mt-1">{campaign.channel.toUpperCase()}</p>
                    </div>
                    <div>
                      <p>Aperturas</p>
                      <p className="text-foreground mt-1">{campaign.opens}</p>
                    </div>
                    <div>
                      <p>Clicks</p>
                      <p className="text-foreground mt-1">{campaign.clicks}</p>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Repeat2 className="size-4 text-cyan-700 dark:text-cyan-400" /> Automatizaciones</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {AUTOMATIONS.map((automation) => (
                <div key={automation.id} className="rounded-xl border border-border bg-[hsl(var(--surface))] p-4 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium">{automation.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{automation.trigger} • {automation.channel.toUpperCase()}</p>
                  </div>
                  <Badge variant={automation.active ? 'success' : 'muted'}>{automation.active ? 'Activa' : 'Pausada'}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="size-4 text-cyan-700 dark:text-cyan-400" /> Métricas rápidas</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InfoTile title="Open rate" description="Promedio visual para comparar campañas." value="47%" />
              <InfoTile title="Click rate" description="Interacciones sobre mensajes enviados." value="12.4%" />
              <InfoTile title="Bajas" description="Usuarios que se dieron de baja este mes." value="0.6%" />
              <InfoTile title="Entregabilidad" description="Mensajes entregados con éxito." value="99.1%" />
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  )
}

function StatCard({
  label,
  value,
  icon: Icon,
  color,
  suffix = '',
}: {
  label: string
  value: number
  icon: React.ComponentType<{ className?: string }>
  color: string
  suffix?: string
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex justify-between items-start gap-4">
          <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold mt-1">{value.toLocaleString('es-CO')}{suffix}</p>
          </div>
          <Icon className={cn('size-6', color)} />
        </div>
      </CardContent>
    </Card>
  )
}

function MetricChip({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-[hsl(var(--surface))] p-3">
      <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className="text-sm text-foreground mt-1">{value}</p>
    </div>
  )
}

function InfoTile({ title, description, value }: { title: string; description: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-[hsl(var(--surface))] p-4">
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-muted-foreground mt-1">{description}</p>
      <p className="text-lg font-semibold text-cyan-700 dark:text-cyan-300 mt-3">{value}</p>
    </div>
  )
}
